import React from 'react';
import { createClient } from '@supabase/supabase-js';
import { supabaseConfig } from './config';

// One Supabase client for the whole app. Uses localStorage so the
// session survives page refresh.
export const supabase = createClient(
  supabaseConfig.url,
  supabaseConfig.anonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }
);

// React context for the current session + user
const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = React.useState(null);
  const [user, setUser] = React.useState(null);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    // Get the current session immediately
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setReady(true);
    });

    // Subscribe to changes (sign-in, sign-out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const value = React.useMemo(
    () => ({
      session,
      user,
      ready,
      // Convenience helpers
      signInWithPassword: (email, password) =>
        supabase.auth.signInWithPassword({ email, password }),
      signUp: (email, password) =>
        supabase.auth.signUp({
          email,
          password,
          // Build the confirmation link back to the same domain the user
          // signed up on (e.g. https://app.datames.dev).
          // Falls back to undefined if window is unavailable (SSR).
          emailRedirectTo:
            typeof window !== 'undefined'
              ? window.location.origin + '/'
              : undefined,
        }),
      signOut: () => supabase.auth.signOut(),
    }),
    [session, user, ready]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthProvider>');
  return ctx;
}
